/**
 * useRegisterForm
 *
 * All form logic for the registration flow — separated from the UI layer.
 * The component only renders; this hook owns validation, state, and submission.
 *
 * Extension point: add fields to `RegisterFields`, add rules to `validate()`.
 */

import { useState, useCallback, type ChangeEvent, type FormEvent } from 'react';
import { useAuth } from './useAuth';
import { ApiError } from '../api/client';
import { getPasswordStrength } from './usePasswordStrength';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RegisterFields {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  countryCode: string;
}

export interface RegisterFieldErrors {
  email?: string;
  username?: string;
  password?: string;
  confirmPassword?: string;
}

export interface UseRegisterFormReturn {
  fields: RegisterFields;
  errors: RegisterFieldErrors;
  serverError: string | null;
  loading: boolean;
  touched: Record<keyof RegisterFields, boolean>;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: FormEvent) => void;
  isSubmittable: boolean;
}

// ── Validation ────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_RE = /^[a-zA-Z0-9_-]+$/;

function validate(fields: RegisterFields): RegisterFieldErrors {
  const errs: RegisterFieldErrors = {};

  if (!fields.email) {
    errs.email = 'Email is required';
  } else if (!EMAIL_RE.test(fields.email)) {
    errs.email = 'Enter a valid email address';
  }

  if (!fields.username) {
    errs.username = 'Username is required';
  } else if (fields.username.length < 3) {
    errs.username = 'Username must be at least 3 characters';
  } else if (fields.username.length > 30) {
    errs.username = 'Username must be 30 characters or less';
  } else if (!USERNAME_RE.test(fields.username)) {
    errs.username = 'Only letters, numbers, _ and - allowed';
  }

  if (!fields.password) {
    errs.password = 'Password is required';
  } else {
    const strength = getPasswordStrength(fields.password);
    if (!strength.isAcceptable) {
      errs.password = 'Password is too weak — satisfy at least 4 of the 5 rules below';
    }
  }

  if (!fields.confirmPassword) {
    errs.confirmPassword = 'Please confirm your password';
  } else if (fields.password !== fields.confirmPassword) {
    errs.confirmPassword = 'Passwords do not match';
  }

  return errs;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

const INITIAL_FIELDS: RegisterFields = {
  email: '',
  username: '',
  password: '',
  confirmPassword: '',
  countryCode: '',
};

const INITIAL_TOUCHED: Record<keyof RegisterFields, boolean> = {
  email: false,
  username: false,
  password: false,
  confirmPassword: false,
  countryCode: false,
};

export function useRegisterForm(): UseRegisterFormReturn {
  const { register } = useAuth();

  const [fields, setFields] = useState<RegisterFields>(INITIAL_FIELDS);
  const [touched, setTouched] = useState(INITIAL_TOUCHED);
  const [serverError, setServerError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Derived — recomputed only when fields change, not on every render
  const allErrors = validate(fields);

  // Only surface errors for fields the user has interacted with
  const errors: RegisterFieldErrors = {
    email:           touched.email           ? allErrors.email           : undefined,
    username:        touched.username        ? allErrors.username        : undefined,
    password:        touched.password        ? allErrors.password        : undefined,
    confirmPassword: touched.confirmPassword ? allErrors.confirmPassword : undefined,
  };

  const isSubmittable =
    Object.keys(allErrors).length === 0 && !loading;

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    setServerError(null);
  }, []);

  const handleBlur = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();

    // Touch all fields to show all errors on submit attempt
    setTouched({ email: true, username: true, password: true, confirmPassword: true, countryCode: true });

    const errs = validate(fields);
    if (Object.keys(errs).length > 0) return;

    setServerError(null);
    setLoading(true);

    register({
      email: fields.email,
      username: fields.username,
      password: fields.password,
      countryCode: fields.countryCode || undefined,
    })
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          if (err.status === 409) {
            const msg = err.message.toLowerCase();
            setServerError(
              msg.includes('email')
                ? 'An account with that email already exists'
                : msg.includes('username')
                  ? 'That username is already taken'
                  : 'Account already exists',
            );
          } else if (err.status === 429) {
            setServerError('Too many attempts — please wait a moment and try again');
          } else {
            setServerError('Registration failed — please try again');
          }
        } else {
          setServerError('Network error — check your connection');
        }
      })
      .finally(() => setLoading(false));
    // Note: on success, useAuth.register() navigates to /auth/verify-email-sent
    // so setLoading(false) in finally still runs but the component unmounts cleanly.
  }, [fields, register]);

  return {
    fields,
    errors,
    serverError,
    loading,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmittable,
  };
}
